import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BellRing, Mail, CheckCircle2, AlertTriangle, Activity } from "lucide-react";

// Shared lead-time choices for both the global default and per-article override.
export const LEAD_CHOICES = [
  { v: 15, label: "15 minutes before" },
  { v: 30, label: "30 minutes before" },
  { v: 60, label: "1 hour before" },
  { v: 120, label: "2 hours before" },
  { v: 1440, label: "1 day before" },
];

export default function ReminderSettings({ open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lead, setLead] = useState(30);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [deliveryTracking, setDeliveryTracking] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/admin/journal/settings")
      .then((r) => {
        setLead(r.data.reminder_lead_minutes || 30);
        setEmailEnabled(!!r.data.email_enabled);
        setEmailConfigured(!!r.data.email_configured);
        setDeliveryTracking(!!r.data.delivery_tracking);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/journal/settings", { reminder_lead_minutes: Number(lead), email_enabled: emailEnabled });
      setEmailConfigured(!!r.data.email_configured);
      toast.success("Reminder settings saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reminder-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BellRing size={18} className="text-[#c9a86a]" /> Go-live reminders</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-[var(--muted)]">Loading…</div>
        ) : (
          <div className="space-y-5">
            <div className="space-y-1.5">
              <Label className="text-xs">Default reminder time</Label>
              <Select value={String(lead)} onValueChange={(v) => setLead(Number(v))}>
                <SelectTrigger className="bg-[var(--panel)]" data-testid="reminder-default-lead"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAD_CHOICES.map((c) => <SelectItem key={c.v} value={String(c.v)}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[var(--muted)]">Applied to scheduled articles that don't set their own reminder time.</p>
            </div>

            <div className="flex items-center justify-between rounded-md border border-[var(--border-lch)] bg-[var(--panel)] p-3">
              <div className="flex items-center gap-2">
                <Mail size={16} className="text-[#c9a86a]" />
                <div>
                  <div className="text-sm font-medium">Email the editors</div>
                  <div className="text-[11px] text-[var(--muted)]">Send the reminder to all Journal admins by email.</div>
                </div>
              </div>
              <Switch checked={emailEnabled} onCheckedChange={setEmailEnabled} data-testid="reminder-email-toggle" />
            </div>

            {emailEnabled && (
              <div className={`flex items-start gap-2 text-xs rounded-md p-2.5 ${emailConfigured ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`} data-testid="reminder-email-status">
                {emailConfigured ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
                <span>{emailConfigured
                  ? "Email provider is connected — reminders will be delivered by email."
                  : "Email provider isn't connected yet. Reminders will be recorded in the Notifications log until the provider is configured."}</span>
              </div>
            )}

            {emailEnabled && (
              <div className={`flex items-start gap-2 text-xs rounded-md p-2.5 ${deliveryTracking ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-[var(--panel)] text-[var(--text-2)] border border-[var(--border-lch)]"}`} data-testid="reminder-delivery-tracking-status">
                <Activity size={14} className="mt-0.5 shrink-0 text-[#c9a86a]" />
                <span>{deliveryTracking
                  ? "Delivery tracking is on — you'll see delivered, opened, and bounced status next to each article's reminder."
                  : "Delivery tracking isn't connected yet. Once the SendGrid Event Webhook is configured, delivered / opened / bounced status will appear next to each reminder."}</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reminder-settings-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || loading} className="bg-[var(--panel-2)] text-[#c9a86a] hover:bg-[var(--border-2)]" data-testid="reminder-settings-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
